import React, { useState, useEffect } from 'react';
import ActivePlanDetails from './ActivePlanDetails';
import PlanChangeModal from './PlanChangeModal';
import ToastContainer from './ToastContainer';
import PlanCard from './PlanCard';
import { useToast } from './useToast';
import {
  getPlans,
  createCheckoutSession,
  previewUpgrade,
  upgradeSubscription,
  scheduleDowngrade,
  cancelPendingChange,
  getSubscriptionStatus,
  invalidateAllCaches
} from '../../api';
import { Plan, SubscriptionStatus } from '../../types/PlanTypes';
import { format } from 'date-fns';



interface PlanChangeInfo {
  pendingPlan: Plan;
  effectiveDate: Date;
  changeType: string;
}

interface AccountsBillingProps {
  onPlanUpdated?: () => void;
}

const AccountsBilling: React.FC<AccountsBillingProps> = ({ onPlanUpdated }) => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [planChangeInfo, setPlanChangeInfo] = useState<PlanChangeInfo | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionStatus | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [isCancelPendingChangeProcessing, setIsCancelPendingChangeProcessing] = useState(false);

  const [planChangeData, setPlanChangeData] = useState<{
    type: 'upgrade' | 'downgrade';
    currentPlan: string;
    newPlan: Plan;
    preview?: any;
  } | null>(null);

  const { toasts, showSuccess, showError, showWarning, removeToast } = useToast();

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoadingPlans(true);
      setPlansError(null);

      // Fetch plans and subscription status in parallel
      const [plansResponse, subscriptionResponse] = await Promise.all([
        getPlans(),
        getSubscriptionStatus().catch(() => null) // Don't fail if no subscription
      ]);

      const fetchedPlans = plansResponse.data || [];

      setPlans(fetchedPlans);

      let subData: SubscriptionStatus | null = null;

      // Set subscription info
      if (subscriptionResponse) {
        subData = subscriptionResponse;

        setSubscriptionInfo(subData);

        const pendingPlanId = subData.pendingPlanId;
        const changeEffectiveDate = subData.planChangeEffectiveDate;
        const changeType = subData.planChangeType;


        if (pendingPlanId && changeEffectiveDate) {
          const pendingPlan = fetchedPlans.find((p: Plan) => p.id === pendingPlanId);
          if (pendingPlan) {
            setPlanChangeInfo({
              pendingPlan,
              effectiveDate: new Date(changeEffectiveDate),
              changeType: changeType || 'change'
            });
          }
        } else {
          setPlanChangeInfo(null);
        }
      }
      else {
        setPlanChangeInfo(null);
      }

      // Set current plan based on API response
      let customerCurrentPlan = null;

      if (subData && subData?.currentPlanId) {
        // Use the current plan ID from the API
        const currentPlanFromApi = fetchedPlans.find((p: Plan) => p.id === subData?.currentPlanId);
        if (currentPlanFromApi) {
          customerCurrentPlan = currentPlanFromApi;
        }
      }

      setCurrentPlan(customerCurrentPlan);

      // Set plan change info for display


    } catch (error) {
      console.error('Error fetching data:', error);
      setPlansError('Failed to load plans. Please try again later.');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const onPlanCanceled = async () => {
    fetchData();
    onPlanUpdated?.();
  }

  const handlePlanPurchase = async (plan: Plan) => {
    if (!plan.stripePriceId) {
      showError('This plan is not available for purchase at this time.');
      return;
    }

    setIsChangingPlan(true);
    try {
      // Get plan ID from plan data
      const planResponse = await getPlans();
      const planData = planResponse.data.find((p: any) => p.id === plan.id);

      if (!planData) {
        throw new Error('Plan not found');
      }

      // Create checkout session
      const checkoutResponse = await createCheckoutSession(planData.id || plan.id);

      // Redirect to Stripe Checkout
      if (checkoutResponse.checkoutUrl) {
        //not guaranteed that they will purchase but clear caches just in case
        invalidateAllCaches();
        //navigate to stripe checkout
        window.location.href = checkoutResponse.checkoutUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      showError(`Failed to start checkout: ${error.message}`);
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handlePlanChange = async (plan: Plan) => {
    if (!currentPlan || !subscriptionInfo?.hasActiveSubscription || subscriptionInfo?.status === 'cancel_at_period_end') {
      // No current subscription, start new subscription
      await handlePlanPurchase(plan);
      return;
    }

    // Get current and new plan prices for comparison
    const currentPlanPrice = currentPlan.priceRaw;
    const newPlanPrice = plan.priceRaw;

    setIsChangingPlan(true);
    try {
      // Get plan ID from the plans array
      const planResponse = await getPlans();
      const planData = planResponse.data.find((p: any) => p.id === plan.id);

      if (!planData) {
        throw new Error('Plan not found');
      }

      if (newPlanPrice > currentPlanPrice) {
        // Upgrade - show confirmation modal with proration details
        try {
          const preview = await previewUpgrade(planData.id || plan.id);
          setPlanChangeData({
            type: 'upgrade',
            currentPlan: currentPlan.name,
            newPlan: plan,
            preview: preview
          });
          setShowPlanChangeModal(true);
        } catch (error: any) {
          console.error('Error previewing upgrade:', error);
          showError(`Failed to preview upgrade: ${error.message}`);
        }
      } else if (newPlanPrice < currentPlanPrice) {
        // Downgrade - show confirmation modal (don't schedule yet)
        setPlanChangeData({
          type: 'downgrade',
          currentPlan: currentPlan.name,
          newPlan: plan,
          preview: {
            effectiveDate: subscriptionInfo?.planEndDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now as fallback
          }
        });
        setShowPlanChangeModal(true);
      } else {
        showWarning('You are already on this plan.');
      }
    } catch (error: any) {
      console.error('Error changing plan:', error);
      showError(`Failed to change plan: ${error.message}`);
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleCancelPendingChange = async () => {
    try {
      setIsCancelPendingChangeProcessing(true);
      await cancelPendingChange();
      setPlanChangeInfo(null);
      showSuccess('Pending plan change has been canceled.');

      // Refresh data after a short delay to show the toast
      setTimeout(() => {
        fetchData();
        // Notify parent component that plan was updated
        onPlanUpdated?.();
      }, 1000);
    } catch (error: any) {
      console.error('Failed to cancel pending change:', error);
      showError(`Failed to cancel pending change: ${error.message}`);
    }
    finally {
      setIsCancelPendingChangeProcessing(false);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeData) return;

    setIsChangingPlan(true);
    try {
      const planResponse = await getPlans();
      const planData = planResponse.data.find((p: any) => p.id === planChangeData.newPlan.id);

      if (!planData) {
        throw new Error('Plan not found');
      }

      if (planChangeData.type === 'upgrade') {
        await upgradeSubscription(planData.id || planChangeData.newPlan.id);
        showSuccess(`Successfully upgraded to ${planChangeData.newPlan.name} plan! You've been charged $${planChangeData.preview.prorationAmount.toFixed(2)}.`);

      } else {
        // For downgrade, schedule it now
        const response = await scheduleDowngrade(planData.id || planChangeData.newPlan.id);
        showSuccess(`Downgrade to ${planChangeData.newPlan.name} plan scheduled for ${new Date(response.effectiveDate).toLocaleDateString()}. You'll continue to have access to your current features until then.`);

      }

      setShowPlanChangeModal(false);
      setPlanChangeData(null);
      fetchData();
      onPlanUpdated?.();
      invalidateAllCaches();

    } catch (error: any) {
      console.error(`Error ${planChangeData.type}ing subscription:`, error);
      showError(`Failed to ${planChangeData.type} subscription: ${error.message}`);
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleClosePlanChangeModal = () => {
    setShowPlanChangeModal(false);
    setPlanChangeData(null);
    setIsChangingPlan(false);
  };



  // Loading state
  if (isLoadingPlans) {
    return (
      <div>
        <h3>Accounts & Billing</h3>
        <div style={{
          textAlign: 'center',
          padding: 40,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
        }}>
          <p>Loading plans...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (plansError) {
    return (
      <div>
        <h3>Accounts & Billing</h3>
        <div style={{
          textAlign: 'center',
          padding: 40,
          background: '#fff',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          border: '1px solid #dc3545'
        }}>
          <p style={{ color: '#dc3545', marginBottom: 16 }}>{plansError}</p>
          <button
            onClick={() => fetchData()}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: 4,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = '#0056b3';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 123, 255, 0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = '#007bff';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
      <h3>Accounts & Billing</h3>

      {/* Pending Plan Change Notification */}
      {planChangeInfo && (
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16
        }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#856404' }}>
            Scheduled Plan Change
          </h4>
          <p style={{ margin: '0 0 12px 0', color: '#856404' }}>
            Your plan will {planChangeInfo.changeType} to{' '}
            <strong>{planChangeInfo.pendingPlan.name}</strong> on{' '}
            {format(planChangeInfo.effectiveDate, 'do MMMM, yyyy')}.
            {planChangeInfo.changeType === 'downgrade' &&
              ' You\'ll continue to have access to your current features until then.'}
          </p>
          <button
            onClick={handleCancelPendingChange}
            style={{
              padding: '8px 16px',
              background: '#856404',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: isCancelPendingChangeProcessing ? 'not-allowed' : 'pointer',
              fontSize: 14,
              transition: 'all 0.2s ease',
              transform: 'scale(1)',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.backgroundColor = '#9d7507';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(133, 100, 4, 0.3)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.backgroundColor = '#856404';
              e.currentTarget.style.boxShadow = 'none';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.95)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            disabled={isCancelPendingChangeProcessing}
          >
            {isCancelPendingChangeProcessing ?
              (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 16,
                    height: 16,
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Processing...
                </div>
              ) :
              (
                'Cancel Scheduled Change'
              )}

          </button>
        </div>
      )}

      {/* Current Plan Overview */}
      <ActivePlanDetails
        planData={subscriptionInfo && currentPlan ? {
          Id: subscriptionInfo?.currentPlanId ?? 0,
          SubscriptionStatus: subscriptionInfo?.status ?? '',
          IsActive: subscriptionInfo?.hasActiveSubscription ?? false,
          PlanStartDate: subscriptionInfo?.planStartDate || '',
          PlanEndDate: subscriptionInfo?.planEndDate || '',
          Type: currentPlan.name,
          RefreshFrequency: currentPlan.syncFrequency,
          MonthsOfData: currentPlan.maxMonthsHistory,
          IncludeMarketInsights: currentPlan.marketInsights,
          TotalIntegrations: currentPlan.maxIntegrations,
          PendingPlanType: subscriptionInfo.pendingPlanType,
          PendingPlanEffectiveDate: subscriptionInfo.planChangeEffectiveDate,
          DiscountCouponName: subscriptionInfo.discountCouponName,
          DiscountEndDate: subscriptionInfo.discountEndDate,
          DiscountStripeId: subscriptionInfo.discountStripeId
        } : null}
        numberOfActiveIntegrations={1}
        enableCancellationFunctionality={
          (subscriptionInfo?.hasActiveSubscription ?? false) &&
          subscriptionInfo?.status === 'active' &&
          !subscriptionInfo.planEndDate}
        onPlanCanceled={onPlanCanceled}
        enableManagePlanNavigationButton={false}
      />

      {/* Plan Selection */}
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}>
        <h4 style={{ margin: '0 0 16px 0' }}>Choose Your Plan</h4>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Select a plan that best fits your organization's needs. You can upgrade or downgrade at any time.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 20,
          marginBottom: 24
        }}>
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={currentPlan?.id === plan.id}
              subscriptionStatus={subscriptionInfo?.status ?? ''}
              planEndDate={subscriptionInfo?.planEndDate ?? ''}
              isChangingPlan={isChangingPlan}
              hasActiveSubscription={subscriptionInfo?.hasActiveSubscription ?? false}
              onPlanChange={handlePlanChange}
            />
          ))}
          {/* Custom Sales Card */}
          <div style={{
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: 20,
            backgroundColor: '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            minHeight: '200px'
          }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>Custom Enterprise</h4>
            <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
              For needs over and above the Enterprise level, contact our sales team for a custom quote.
            </p>
            <button
              onClick={() => window.open('mailto:sales@execdash.ai?subject=Custom Enterprise Quote Request', '_blank')}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 4,
                fontSize: 14,
                cursor: 'pointer',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                transform: 'scale(1)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.backgroundColor = '#5a6268';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(108, 117, 125, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = '#6c757d';
                e.currentTarget.style.boxShadow = 'none';
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
            >
              Contact Sales
            </button>
          </div>
        </div>

        {/* Plan Change Modal */}
        {planChangeData && (
          <PlanChangeModal
            isOpen={showPlanChangeModal}
            onClose={handleClosePlanChangeModal}
            onConfirm={handleConfirmPlanChange}
            isProcessing={isChangingPlan}
            changeType={planChangeData.type}
            currentPlan={planChangeData.currentPlan}
            newPlan={planChangeData.newPlan.name}
            planDetails={{
              effectiveDate: planChangeData.type === 'upgrade'
                ? new Date().toISOString()
                : planChangeData.preview.effectiveDate,
              amount: planChangeData.type === 'upgrade'
                ? planChangeData.preview.prorationAmount
                : 0,
              nextBillingDate: planChangeData.type === 'upgrade'
                ? planChangeData.preview.nextBillingDate
                : '',
              newPlanPrice: planChangeData.newPlan.priceString
            }}
          />
        )}

      </div>
    </div>
  );
};

export default AccountsBilling;
